
import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { identity, type UserIdentity } from "../http/authProvider";
import { httpClient, parseJwt } from "../http/httpClient";
import { useWebsite } from "./useWebsite";

interface AccountContextType {
    account: UserIdentity;
    setAccount: (account: UserIdentity) => void;
}

export const AccountContext = createContext<AccountContextType | undefined>(undefined);

export const AccountProvider = ({ children }: { children: ReactNode }) => {
    const { accountId } = useWebsite();
    const [account, setAccount] = useState<UserIdentity>(() => {
        try {
            const token = localStorage.getItem("access_token");
            if (token && accountId) { 
                return identity(accountId, parseJwt(token));
            }
        } catch (err) {
            console.error("Failed to parse website cache:", err);
        }
        return { id: 0, role: "None" };
    });
    const handleLogin = async (e: CustomEvent) => {
        const user = parseJwt(e.detail.token);
        const contactId = user.app_metadata.access[0];
        if(accountId !== contactId) {
            httpClient(`/api/rest/v1/contact`, { method: "POST", body: JSON.stringify({ accountId, contactId }) });
        }
        const orderId = localStorage.getItem("RaStore.orderId");
        if(orderId && accountId !== contactId) {
            await httpClient(`/api/rest/v1/order?id=eq.${accountId}-0-${orderId}`, { method: "PATCH", body: JSON.stringify({ buyerId: contactId }) });
        }
        setAccount(identity(accountId, user));
    };
    useEffect(() => {
        window.addEventListener("login", handleLogin as any);
        return () => window.removeEventListener("login", handleLogin as any);
    },[]);
    return (
        <AccountContext.Provider value={{ account, setAccount }}>
            {children}
        </AccountContext.Provider>
    );
};

export const useAccount = () => {
    const context = useContext(AccountContext)?.account;
    if (context === undefined) {
        throw new Error("useGetAccount must be used within an AccountProvider");
    }
    return context;
};
